/**
 * Create Challenge API Functions
 * API calls for fetching games and friends
 */

import axios from 'axios';
import { buildEndpointUrl, API_URL, API_ENDPOINTS } from '../../../constants';
import { normalizeTeamKitType } from '../../../constants/teamKitColors';
import type { Game, Friend } from '../types/create.types';
import { formatDateTime } from '../../../helpers/formatDateTime.helper';
import { getDisplayAvailablePicksCount } from './availablePicksCount.helper';
import { DEFAULT_AVAILABLE_BETS_BADGE } from '../constants/matchDetails.constants';
import { resolveLeagueCountryCodeForFlag } from '../../shared/helpers/leagueCountryToFlagCode.helper';
import { formatLeagueName } from '../../shared/helpers/leagueDisplayName.helper';
import { getSportDefinitionOrDefault, listSportKeys, OUTCOME_SHAPES } from '../../sports/sports.constants';

/**
 * Sports offered in the challenge create flow, as the `sport` query parameter
 * (specs/multi-sport-challenges). Every registered sport is requested, so adding one to the
 * registry makes its fixtures selectable without touching this file.
 *
 * The endpoint defaults to football when the parameter is absent, so an older app build keeps
 * seeing football only.
 */
const CREATE_FLOW_SPORTS = listSportKeys().join(',');

/** Max matches in fallback when date-range request fails. Must stay ≤ server MATCH_LIST_MAX to avoid OOM (huge JSON: matches + teams + leagues + odds). */
const MATCHES_FALLBACK_LIMIT = 200;

/** Kit config from API (matches list attaches homeKit/awayKit from teams). */
type MatchKitPayload = { primary: string; secondary: string; type: string };

/** Match payload from API (matches list response). League may include id and displayOrder for sorting. */
type MatchPayload = {
  id: string;
  /** Sport key; absent on an older server response = football. */
  sport?: string | null;
  date: string;
  homeTeam?: { name: string };
  awayTeam?: { name: string };
  league?: {
    name?: string;
    id?: string;
    displayOrder?: number | null;
    country?: string;
  };
  odds?: {
    home?: number | null;
    away?: number | null;
    draw?: number | null;
    /** FIELD sports only — how many entrants the primary market prices. */
    fieldOutcomeCount?: number;
    isSynthetic?: boolean;
    markets?: MatchDetailsOddsMarket[];
  };
  homeKit?: MatchKitPayload;
  awayKit?: MatchKitPayload;
  /** FIELD sports: the Grand Prix name. Football stores its league round here. */
  round?: string | null;
  /** FIELD sports: the circuit. */
  venue?: string | null;
};

/** Single market in odds.markets (match details / API-Football v3). */
export type MatchDetailsOddsMarket = {
  id: number;
  name: string;
  values: Array<{ value: string; odd: number }>;
};

/** Match details API response (single match by id, includes odds with optional markets). */
export type MatchDetailsPayload = {
  id: string;
  date: string;
  homeTeam?: { name: string };
  awayTeam?: { name: string };
  league?: { name: string };
  odds?: {
    home?: number;
    draw?: number;
    away?: number;
    markets?: MatchDetailsOddsMarket[];
  };
};

/**
 * Parse YYYY-MM-DD and return user's local midnight (00:00:00.000) and end of day (23:59:59.999).
 * Uses Date(year, monthIndex, day) so the engine uses the device's local timezone, not UTC.
 */
export const getLocalDayRange = (
  dateStr: string
): { startMs: number; endMs: number } | null => {
  const parts = dateStr.split('-').map(Number);
  if (parts.length !== 3) return null;
  const [y, m, d] = parts;
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  const start = new Date(y, m - 1, d);
  const end = new Date(y, m - 1, d, 23, 59, 59, 999);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return { startMs: start.getTime(), endMs: end.getTime() };
};

/**
 * Get start/end timestamps for a range of calendar days (inclusive).
 * from and to are YYYY-MM-DD; uses local midnight so the user's day boundaries are correct.
 */
export const getLocalRangeTimestamps = (
  from: string,
  to: string
): { startMs: number; endMs: number } | null => {
  const fromRange = getLocalDayRange(from);
  const toRange = getLocalDayRange(to);
  if (!fromRange || !toRange || fromRange.startMs > toRange.endMs) return null;
  return { startMs: fromRange.startMs, endMs: toRange.endMs };
};

function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function transformMatchToGame(match: MatchPayload): Game {
  const raw = match.date != null ? String(match.date).trim() : '';
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw);
  let parsed: Date | null = null;
  if (raw) {
    if (isDateOnly) {
      const [y, m, d] = raw.split('-').map(Number);
      parsed = new Date(y, m - 1, d, 12, 0, 0);
    } else {
      parsed = new Date(match.date!);
    }
  }
  const isValid = parsed != null && !Number.isNaN(parsed.getTime());
  const dateStr = isValid ? toLocalDateStr(parsed!) : toLocalDateStr(new Date());
  let timeStr = isValid ? formatDateTime(parsed!) : '';
  if (isValid && !timeStr) timeStr = '12:00';
  // Set hasRealOdds before applying fallback so we can tell real vs placeholder (server sends null when no Odds row).
  const hasRealOdds = match.odds != null;
  // Odds are hydrated by the server (home, draw, away derived from odds.markets when needed). Fallback only for display; create flow filters by hasRealOdds.
  // The draw tile follows the PRIMARY MARKET, not the sport's final-score rule
  // (specs/basketball-regulation-1x2). Basketball's headline market is the regulation 1X2, which
  // has a genuine draw, even though the sport's final score can never be level — so keying this on
  // `allowsDraw` would hide a tile the market actually offers.
  const sportDefinition = getSportDefinitionOrDefault(match.sport);
  // A FIELD sport has no sides, so it gets no home/away price — not even a fallback. The `?? 2.0`
  // below is a display placeholder for a two-sided fixture whose odds row is missing; applying it
  // to a race would paint a fabricated 2.00 HOME/AWAY row on every Grand Prix, and that number is
  // reachable by the pick-building helpers (specs/formula-one-challenges AC-1.7, AC-1.8).
  const isFieldSport = sportDefinition.shape === OUTCOME_SHAPES.FIELD;
  // The 3.0 fallback is FOOTBALL LEGACY and stays football-only: thousands of live rows rely on a
  // missing 1X2 price defaulting rather than rendering blank. Basketball must never invent one —
  // an unpriced regulation market means the draw is not on offer for that fixture, and a fabricated
  // 3.00 would be indistinguishable from a real price to every consumer downstream.
  const drawPrice = sportDefinition.primaryMarketHasDraw
    ? match.odds?.draw ?? (sportDefinition.allowsDraw ? 3.0 : undefined)
    : undefined;
  const odds = isFieldSport
    ? {
        home: null,
        away: null,
        draw: undefined,
        fieldOutcomeCount: match.odds?.fieldOutcomeCount,
      }
    : {
        home: match.odds?.home ?? 2.0,
        away: match.odds?.away ?? 2.0,
        draw: drawPrice ?? undefined,
      };
  const leagueCountryCode =
    resolveLeagueCountryCodeForFlag(match.league?.country, match.league?.name) ?? undefined;
  return {
    id: match.id,
    sport: sportDefinition.key,
    // A FIELD fixture has no sides. Naming a race "Unknown vs Unknown" would be a lie in any
    // generic consumer, so both sides carry the event's own name instead.
    homeTeam: match.homeTeam?.name || (isFieldSport ? (match.round ?? 'Race') : 'Unknown'),
    awayTeam: match.awayTeam?.name || (isFieldSport ? (match.venue ?? '') : 'Unknown'),
    ...(isFieldSport
      ? { eventName: match.round ?? undefined, venue: match.venue ?? undefined }
      : {}),
    league: formatLeagueName(match.league?.name),
    leagueId: match.league?.id ?? undefined,
    leagueCountryCode,
    leagueDisplayOrder: match.league?.displayOrder ?? Number.MAX_SAFE_INTEGER,
    date: dateStr,
    time: timeStr,
    rawDate: raw || undefined,
    odds,
    homeKit: match.homeKit
      ? {
          primary: match.homeKit.primary,
          secondary: match.homeKit.secondary,
          type: normalizeTeamKitType(match.homeKit.type),
        }
      : undefined,
    awayKit: match.awayKit
      ? {
          primary: match.awayKit.primary,
          secondary: match.awayKit.secondary,
          type: normalizeTeamKitType(match.awayKit.type),
        }
      : undefined,
    availablePicksCount:
      match.odds?.markets != null
        ? getDisplayAvailablePicksCount(match.odds.markets)
        : DEFAULT_AVAILABLE_BETS_BADGE,
    hasRealOdds,
    oddsAreEstimated: match.odds?.isSynthetic === true,
    ...(match.odds?.markets != null && match.odds.markets.length > 0
      ? { oddsMarkets: match.odds.markets }
      : {}),
  };
}

/**
 * Fetch games for a single calendar day. Uses local midnight so the range is the user's day.
 * On failure (network/4xx/5xx), throws so the caller can avoid caching and retry later.
 */
export const fetchGamesByDate = async (dateStr: string): Promise<Game[]> => {
  const range = getLocalDayRange(dateStr);
  if (!range) return [];

  const startIso = new Date(range.startMs).toISOString();
  const endIso = new Date(range.endMs).toISOString();
  const url = `${buildEndpointUrl('matches', 'all')}?start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}&sport=${CREATE_FLOW_SPORTS}`;
  try {
    const response = await axios.get<{ success?: boolean; data?: MatchPayload[] }>(url);
    const data = response.data;
    if (data?.success === true && Array.isArray(data.data)) {
      return data.data.map(transformMatchToGame);
    }
    return [];
  } catch (err) {
    const fallbackUrl = `${buildEndpointUrl('matches', 'all')}?limit=${MATCHES_FALLBACK_LIMIT}&sport=${CREATE_FLOW_SPORTS}`;
    try {
      const response = await axios.get<{ success?: boolean; data?: MatchPayload[] }>(fallbackUrl);
      const data = response.data;
      if (data?.success === true && Array.isArray(data.data)) {
        const dateStrPrefix = dateStr + 'T';
        const forDay = data.data.filter((m) => {
          if (m.date == null) return false;
          const iso = typeof m.date === 'string' ? m.date : new Date(m.date).toISOString();
          return iso.startsWith(dateStrPrefix) || (iso.slice(0, 10) === dateStr);
        });
        return forDay.map(transformMatchToGame);
      }
    } catch {
      // ignore fallback failure
    }
    throw err;
  }
};

/**
 * Fetch games for a date range in one request. Returns a map of dateStr -> games for each day.
 * Uses local midnight boundaries. On failure throws so caller can avoid caching.
 */
export const fetchGamesByDateRange = async (
  fromStr: string,
  toStr: string
): Promise<Map<string, Game[]>> => {
  const range = getLocalRangeTimestamps(fromStr, toStr);
  if (!range) return new Map();

  const startIso = new Date(range.startMs).toISOString();
  const endIso = new Date(range.endMs).toISOString();
  const url = `${buildEndpointUrl('matches', 'all')}?start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}&sport=${CREATE_FLOW_SPORTS}`;
  try {
    const response = await axios.get<{ success?: boolean; data?: MatchPayload[] }>(url);
    const data = response.data;
    if (data?.success === true && Array.isArray(data.data)) {
      const games = data.data.map(transformMatchToGame);
      const byDate = new Map<string, Game[]>();
      games.forEach((game) => {
        const key = game.date;
        if (!byDate.has(key)) byDate.set(key, []);
        byDate.get(key)!.push(game);
      });
      return byDate;
    }
    return new Map();
  } catch (err) {
    const fallbackUrl = `${buildEndpointUrl('matches', 'all')}?limit=${MATCHES_FALLBACK_LIMIT}&sport=${CREATE_FLOW_SPORTS}`;
    try {
      const response = await axios.get<{ success?: boolean; data?: MatchPayload[] }>(fallbackUrl);
      const data = response.data;
      if (data?.success === true && Array.isArray(data.data)) {
        const startMs = range.startMs;
        const endMs = range.endMs;
        const inRange = data.data.filter((m) => {
          if (m.date == null) return false;
          const ms = new Date(m.date).getTime();
          return ms >= startMs && ms <= endMs;
        });
        const games = inRange.map(transformMatchToGame);
        const byDate = new Map<string, Game[]>();
        games.forEach((game) => {
          const key = game.date;
          if (!byDate.has(key)) byDate.set(key, []);
          byDate.get(key)!.push(game);
        });
        return byDate;
      }
    } catch {
      // ignore fallback failure
    }
    throw err;
  }
};

/**
 * Fetch date strings (YYYY-MM-DD) that have at least one match in the range. For calendar dots.
 * from/to are YYYY-MM-DD; range uses local midnight boundaries.
 */
export const fetchMatchDates = async (
  from: string,
  to: string
): Promise<string[]> => {
  try {
    const range = getLocalRangeTimestamps(from, to);
    if (!range) return [];

    const startIso = new Date(range.startMs).toISOString();
    const endIso = new Date(range.endMs).toISOString();
    const url = `${buildEndpointUrl('matches', 'dates')}?start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}&sport=${CREATE_FLOW_SPORTS}`;
    const response = await axios.get<{ success: boolean; data?: string[] }>(url);
    const data = response.data;

    if (data?.success && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  } catch {
    return [];
  }
};

/**
 * Fetch real friends from database
 * Note: This function is called from component, userId will be passed
 */
export const fetchFriends = async (userId: string): Promise<Friend[]> => {
  try {
    const friendsUrl = buildEndpointUrl('users', 'friends') + `/${userId}`;

    const response = await axios.get<{ success: boolean; data?: any[] }>(friendsUrl, {
      headers: {
        Authorization: `Bearer ${userId}`,
      },
    });

    const data = response.data;

    if (data?.success && data.data && data.data.length > 0) {
      return data.data
        .map((friend: {
          id: string;
          name?: string;
          firstName?: string;
          lastName?: string;
          avatar?: string;
          avatarId?: string | null;
          email?: string;
        }) => {
          let name = friend.name || `${friend.firstName || ''} ${friend.lastName || ''}`.trim();

          if (!name || name.startsWith('http://') || name.startsWith('https://') || name.length > 80) {
            if (friend.email && !friend.email.includes('@placeholder.com')) {
              name = friend.email.split('@')[0];
            } else {
              name = 'Friend';
            }
          }

          return {
            id: friend.id,
            name: name || 'Unknown',
            avatar: friend.avatar || '👤',
            avatarId: friend.avatarId ?? null,
            isSelected: false,
          };
        })
        .filter((friend: Friend) => !friend.name.startsWith('http://') && !friend.name.startsWith('https://'));
    }

    return [];
  } catch {
    return [];
  }
};

export type MatchH2HPayload = {
  fixtures: unknown[];
};

export type MatchLineupsPayload = {
  lineups: unknown[] | null;
};

/**
 * Fetch H2H fixture history for a match (lazy, server-side cached 24h).
 * Returns null on error.
 */
export const fetchMatchH2H = async (matchId: string): Promise<MatchH2HPayload | null> => {
  if (!matchId) return null;
  try {
    const url = `${API_URL}${API_ENDPOINTS.matches.h2h(matchId)}`;
    const response = await axios.get<{ success?: boolean; data?: MatchH2HPayload }>(url);
    if (response.data?.success === true && response.data?.data) {
      return response.data.data;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Fetch lineups for a match (lazy, server-side cached 1h).
 * Returns { lineups: null } when match is too far in the future.
 * Returns null on error.
 */
export const fetchMatchLineups = async (matchId: string): Promise<MatchLineupsPayload | null> => {
  if (!matchId) return null;
  try {
    const url = `${API_URL}${API_ENDPOINTS.matches.lineups(matchId)}`;
    const response = await axios.get<{ success?: boolean; data?: MatchLineupsPayload }>(url);
    if (response.data?.success === true && response.data?.data !== undefined) {
      return response.data.data;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Fetch a single match by id (internal match UUID). Used by Match Details screen for real odds.markets.
 * Returns null on 404 or network error.
 */
export const fetchMatchById = async (
  matchId: string
): Promise<MatchDetailsPayload | null> => {
  if (!matchId || typeof matchId !== 'string' || matchId.trim().length === 0) {
    return null;
  }
  const url = `${buildEndpointUrl('matches', 'all')}/${encodeURIComponent(matchId.trim())}`;

  try {
    const response = await axios.get<{ success?: boolean; data?: MatchDetailsPayload }>(url);
    if (response.data?.success === true && response.data?.data) {
      return response.data.data;
    }
    return null;
  } catch {
    return null;
  }
};

